import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { faPen, faPlusCircle, faSearch, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';
import { indicate } from 'src/app/operators';
import { DictionaryDeleteComponent } from 'src/app/admintool/piping/dictionaries/dialogs/dictionary-delete/dictionary-delete.component';
import {
  ConsolidationMappingViewModel,
  ConsolidationMappingFieldType,
  EsraPipingAPIClient,
  PipingObjectType,
} from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { getApiErrorMessage } from 'src/app/shared/utils';
import { UserService } from 'src/app/core/services/user.service';
import { Constants } from 'src/app/shared/constants/constants';
import { ConsolidationMappingAddEditComponent, ConsolidationMappingAddEditDialogData } from './dialogs/consolidation-mapping-add-edit/consolidation-mapping-add-edit.component';
import { MAPPING_FIELD_TYPE_OPTIONS, PIPING_OBJECT_TYPE_OPTIONS } from './consolidation-mapping-dictionary.model';

/**
 * Admin screen for managing Consolidation Mappings used by ESRA Search filters.
 * A Consolidation Mapping groups multiple document values (`originalValue`) under a
 * single filter value (`mappedValue`) shown to end users in search filter dropdowns.
 *
 * Filtering and paging are delegated to the backend (`GET /api/consolidation-mappings`)
 * rather than using the shared `DictionarySimplePageComponent` client-side filter pattern,
 * because the backend already supports server-side paging/sorting for this dataset.
 */
@Component({
    selector: 'app-consolidation-mapping',
    templateUrl: './consolidation-mapping.component.html',
    styleUrls: ['./consolidation-mapping.component.scss'],
    standalone: false
})
export class ConsolidationMappingComponent implements OnInit {
  readonly pipingObjectTypeOptions = PIPING_OBJECT_TYPE_OPTIONS;
  readonly mappingFieldTypeOptions = MAPPING_FIELD_TYPE_OPTIONS;

  /** Currently selected Component filter (`undefined` = all components). */
  selectedPipingObjectType: PipingObjectType | undefined;
  /** Currently selected Field Type filter (`undefined` = all field types). */
  selectedMappingFieldType: ConsolidationMappingFieldType | undefined;

  currentPage: number = 0;
  pageSize: number = 20;
  readonly pageSizes: number[] = [10, 20, 50, 100];
  totalCount: number = 0;

  dataSource: MatTableDataSource<ConsolidationMappingViewModel> = new MatTableDataSource<ConsolidationMappingViewModel>();
  readonly displayedColumns: string[] = ['pipingObjectType', 'mappingFieldType', 'originalValue', 'mappedValue', 'actions'];

  searching$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  readonly faSearch = faSearch;
  readonly faTimes = faTimes;
  readonly faPlusCircle = faPlusCircle;
  readonly faEdit = faPen;
  readonly faTrashCan = faTrash;

  /** True when the current user may create/update/delete mappings (Admin or Piping.Dictionary.Write). */
  readonly canEdit$: Observable<boolean> = this.userService.roles$.pipe(
    map(roles => roles.includes(Constants.admin) || roles.includes(Constants.pipingDictionaryWriter))
  );

  constructor(
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly insightsService: InsightsService,
    private readonly userService: UserService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.search();
  }

  /**
   * Fetches consolidation mappings from the backend using the current filter,
   * search text, and paging state.
   */
  search(): void {
    const searchText: string = this.searchInput?.nativeElement.value?.trim() ?? '';

    this.esraApiClient.getConsolidationMappings(
      searchText || undefined,
      undefined,
      this.selectedPipingObjectType,
      this.selectedMappingFieldType,
      this.currentPage,
      this.pageSize,
      'originalvalue',
      false
    ).pipe(
      indicate(this.searching$),
      first(),
      tap(result => {
        this.dataSource.data = result.items ?? [];
        this.totalCount = result.totalCount ?? 0;
      })
    ).subscribe({
      error: (err: unknown) => {
        this.snackBarService.showError(getApiErrorMessage(err, 'Failed to load consolidation mappings.'));
        this.insightsService.logException(err as Error);
      }
    });
  }

  setSearchValueAndSearch(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.currentPage = 0;
      this.search();
    }
  }

  clearSearchText(): void {
    this.searchInput.nativeElement.value = '';
    this.currentPage = 0;
    this.search();
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.search();
  }

  updatePage(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.search();
  }

  /** Opens the Add Mapping dialog. On success, refreshes the grid. */
  createNew(): void {
    const dialogData: ConsolidationMappingAddEditDialogData = {
      mapping: null,
      defaultPipingObjectType: this.selectedPipingObjectType,
      defaultMappingFieldType: this.selectedMappingFieldType,
    };

    this.dialog.open(ConsolidationMappingAddEditComponent, {
      width: '600px',
      maxWidth: '95vw',
      panelClass: 'consolidation-mapping-modal',
      data: dialogData,
    }).afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.search();
      }
    });
  }

  /** Opens the Edit Mapping dialog for the given row. On success, refreshes the grid. */
  editItem(mapping: ConsolidationMappingViewModel): void {
    const dialogData: ConsolidationMappingAddEditDialogData = { mapping };

    this.dialog.open(ConsolidationMappingAddEditComponent, {
      width: '600px',
      maxWidth: '95vw',
      panelClass: 'consolidation-mapping-modal',
      data: dialogData,
    }).afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.search();
      }
    });
  }

  /** Opens the delete confirmation dialog. On confirm, deletes the mapping and refreshes the grid. */
  deleteItem(mapping: ConsolidationMappingViewModel): void {
    this.dialog.open(DictionaryDeleteComponent, {
      width: '420px',
      data: {
        item: mapping,
        objectName: `${mapping.mappedValue} -> ${mapping.originalValue}`,
        objectType: 'consolidation mapping',
      },
    }).afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.esraApiClient.deleteConsolidationMapping(mapping.consolidationMappingRecordId).subscribe({
          next: () => {
            this.snackBarService.showInfo(
              'Mapping deleted. Search index refresh has been triggered. Changes may take a few minutes to appear in search results.'
            );
            this.search();
          },
          error: (err: unknown) => {
            this.snackBarService.showError(getApiErrorMessage(err, 'Failed to delete the consolidation mapping.'));
            this.insightsService.logException(err as Error);
          },
        });
      }
    });
  }
}
